import React from "react";
import SiteHeader from "../components/SiteHeader.jsx";
import SiteFooter from "../components/SiteFooter.jsx";

export default function CookiePolicy() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 py-14 flex-1" data-testid="cookie-policy-page">
        <div className="text-xs tracking-widest uppercase text-[#111827] font-semibold">Legal</div>
        <h1 className="font-heading text-4xl font-extrabold text-[#111827] mt-2">Cookie Policy</h1>
        <p className="text-sm text-slate-500 mt-2">Last updated: July 2026</p>

        <div className="prose prose-slate max-w-none mt-8 text-slate-700 leading-relaxed space-y-6">
          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">1. Purpose of this policy</h2>
            <p>
              This Cookie Policy explains how RapidCare SA ("we", "us", "our") uses cookies, browser <em>local storage</em>, and similar technologies on the RapidCare SA web platform. It is aligned with the Protection of Personal Information Act, 2013 (POPIA) of South Africa and should be read alongside our <a href="/privacy" className="text-[#111827] underline">Privacy Policy</a> and <a href="/terms" className="text-[#111827] underline">Terms &amp; Conditions</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">2. What are cookies?</h2>
            <p>
              A <strong>cookie</strong> is a small text file stored on your device by your browser when you visit a website. Cookies let the site remember things about you — such as that you are signed in — across page views and visits. <strong>Local storage</strong> (also known as <code>window.localStorage</code>) is a similar but separate browser mechanism that we use for most of our sign-in state instead of cookies. For simplicity, "cookies" in this policy refers to both cookies and comparable local-storage keys.
            </p>
            <p>
              Cookies can be:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>First-party</strong> — set by RapidCare SA itself.</li>
              <li><strong>Third-party</strong> — set by services we use, such as our payment provider Payfast and Google (Google Analytics, Google Tag Manager and Google Ads).</li>
              <li><strong>Session</strong> — deleted when you close the browser.</li>
              <li><strong>Persistent</strong> — kept for a defined period unless you clear them.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">3. Essential authentication cookies</h2>
            <p>These are strictly necessary to let you sign in and use the platform. They cannot be switched off and do not require consent under POPIA because they are essential to the service you requested.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><code className="px-1 bg-slate-100 rounded">rcs-auth</code> (localStorage) — stores your signed JWT authentication token so you stay logged in between pages.</li>
              <li>Single-session enforcement — a server-side record of your current session that invalidates any older login on a different device to prevent credential sharing.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">4. Security and session cookies</h2>
            <p>Used to keep your session safe and to prevent abuse:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Rate-limiting identifiers — to detect and block brute-force login attempts.</li>
              <li>Session fingerprint — to prevent session fixation and hijacking.</li>
              <li>CSRF tokens, where applicable.</li>
            </ul>
            <p>These are essential security measures and cannot be disabled while you are logged in.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">5. Payment-provider cookies (Payfast)</h2>
            <p>
              When you start a subscription or make a payment, you are redirected to our payment provider, <strong>Payfast</strong>. Payfast sets its own cookies on its own domain to:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Process the card transaction securely (PCI-DSS compliant).</li>
              <li>Detect and prevent payment fraud.</li>
              <li>Remember your bank's 3-D-Secure / OTP step within a single checkout.</li>
            </ul>
            <p>
              We do not receive or store your card number. Payfast's cookie practices are governed by their own cookie notice — see{" "}
              <a href="https://payfast.io/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#111827] underline">payfast.io/privacy-policy</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">6. Analytics &amp; advertising cookies (Google)</h2>
            <p>
              We use Google services, loaded via <strong>Google Tag Manager</strong> (container <code className="px-1 bg-slate-100 rounded">GTM-TBMV58CG</code>), to understand how the platform is used and to measure the effectiveness of our advertising:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Google Analytics 4</strong> (<code className="px-1 bg-slate-100 rounded">G-LB8RHX6SCD</code>) — sets cookies (e.g. <code className="px-1 bg-slate-100 rounded">_ga</code>, <code className="px-1 bg-slate-100 rounded">_ga_*</code>) to measure aggregate usage such as page views, sessions and general device/region information, so we can improve the platform.</li>
              <li><strong>Google Ads conversion tracking</strong> — when you complete a subscription payment, a conversion event is recorded so we can measure which adverts led to a sign-up. This may set Google advertising cookies and is used to measure and optimise our ad campaigns.</li>
            </ul>
            <p>
              These are <strong>non-essential</strong> cookies. We <strong>never</strong> send clinical data, patient data or special personal information to Google, and we do not use these tools to profile you as an individual clinician for third parties. Google processes this data in line with its own policies — see{" "}
              <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer" className="text-[#111827] underline">Google's cookie notice</a> and{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#111827] underline">Google's privacy policy</a>. You can opt out of Google Analytics across all sites using the{" "}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#111827] underline">Google Analytics opt-out browser add-on</a>.
            </p>
            <p className="mt-3 text-sm text-slate-600">
              We also use <strong>PostHog</strong> for product analytics and session replay (with text inputs masked). See our <a href="/privacy" className="text-[#111827] underline">Privacy Policy</a> for details.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">7. Optional preference cookies</h2>
            <p>These remember your personal preferences and are only stored if you interact with the relevant settings:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><code className="px-1 bg-slate-100 rounded">rcs-theme</code> (localStorage) — your UI theme choice, if applicable.</li>
              <li><code className="px-1 bg-slate-100 rounded">rcs-pwa-install-dismissed-v1</code> — remembers that you dismissed the "Install RapidCare SA" banner.</li>
              <li><code className="px-1 bg-slate-100 rounded">rcs-cookie-consent-v2</code> — stores your cookie-consent choice and the timestamp at which it was made (for compliance audit purposes).</li>
            </ul>
            <p>You can decline these at any time — the core platform still works without them.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">8. Cookies do not collect sensitive clinical or patient data</h2>
            <p>
              <strong>RapidCare SA's cookies and local-storage keys never contain clinical data, patient data, or special personal information.</strong> They hold only technical identifiers (e.g. your auth token, your consent choice, your preferences). You should never enter patient-identifiable information into any feature of the platform, and you should never rely on cookies or local storage to retain it.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">9. Your consent choices</h2>
            <p>When you first visit RapidCare SA, you are shown a consent banner with three options:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Accept cookies</strong> — enables essential, security, and optional-preference cookies.</li>
              <li><strong>Reject non-essential cookies</strong> — keeps only the strictly necessary cookies required to sign you in and keep you safe; disables preference cookies and any future analytics.</li>
              <li><strong>View cookie policy</strong> — brings you here.</li>
            </ul>
            <p>
              Your choice is stored in your browser under the key <code className="px-1 bg-slate-100 rounded">rcs-cookie-consent-v2</code> together with a timestamp for compliance audit purposes. You can change your decision at any time by clearing that key in your browser and refreshing the page, or by contacting us.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">10. How to clear cookies</h2>
            <p>
              You can clear cookies and local storage through your browser's settings:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Chrome / Edge: Settings → Privacy and security → Cookies and other site data.</li>
              <li>Safari: Settings → Privacy → Manage Website Data.</li>
              <li>Firefox: Settings → Privacy &amp; Security → Cookies and Site Data.</li>
            </ul>
            <p>
              Clearing essential cookies will sign you out and will require you to log in again.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">11. Your rights under POPIA</h2>
            <p>
              Under the Protection of Personal Information Act, 2013, you have the right to access, correct, or delete your personal information and to withdraw consent at any time. See our <a href="/privacy" className="text-[#111827] underline">Privacy Policy</a> for full detail on how to exercise these rights, or email us at <a href="mailto:rapidcaresasupport@gmail.com" className="text-[#111827] underline">rapidcaresasupport@gmail.com</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">12. Changes to this policy</h2>
            <p>
              We may revise this Cookie Policy as our use of cookies evolves. When we do, we will update the date at the top of this page and, for material changes, may ask you to renew your consent through the banner.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">13. Contact</h2>
            <p>
              For any cookie-related questions, email{" "}
              <a href="mailto:rapidcaresasupport@gmail.com" className="text-[#111827] underline">rapidcaresasupport@gmail.com</a>.
            </p>
          </section>

          <section className="border-t border-slate-200 pt-4 text-xs text-slate-500">
            © {new Date().getFullYear()} RapidCare SA. All rights reserved. See our <a href="/privacy" className="text-[#111827] underline">Privacy Policy</a> and <a href="/terms" className="text-[#111827] underline">Terms &amp; Conditions</a> for more.
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
